import { Checkbox } from '@consta/uikit/Checkbox'
import { Radio } from '@consta/uikit/Radio'
import { selectTabElementSelectionType } from '@core/core/store'
import type { ObjectTreeNode, SelectionType, TabObject } from '@core/core/types'
import { AllTreeIcons } from '@core/icons'
import type { TabType } from '@library/model'
import { isObject, logIfNotTest } from 'common/helpers'
import type {
  BaseObject,
  Nullable,
  ProjectElementInfo,
  ProjectElementPropertyInfo,
  TabElementStateType,
} from 'common/types'
import type { DataNode } from 'rc-tree/lib/interface'
import type { ChangeEventHandler, ReactElement } from 'react'

import { css, CustomLabel } from '../CustomLabel'
import type { ChangeMenuVisibilityFuncType, OnChangeStateChecked } from '../types'

const getFillColor = (properties?: ProjectElementPropertyInfo[]) =>
  properties?.find(prop => prop.propertyType === 'FillColor')?.value

const getCheckComponent = (
  activeTabType: TabType | undefined,
  elementType: BaseObject,
  onChangeStateChecked: OnChangeStateChecked,
  state?: TabElementStateType,
  disabled?: boolean,
) => {
  const selectionType: Nullable<SelectionType | undefined> = selectTabElementSelectionType(
    elementType,
    activeTabType,
  )

  const onRadioClick = () => {
    onChangeStateChecked({ e: undefined, checked: state !== 'Checked', indeterminateState: false })
  }
  const onCheckboxClick: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    const indeterminateState = state === 'Indeterminate'
    onChangeStateChecked({ e: undefined, checked: target.checked, indeterminateState })
  }
  if (!selectionType) return

  return selectionType === 'RadioButton' ? (
    <Radio
      className={css.check}
      size="xs"
      checked={state === 'Checked'}
      disabled={disabled}
      onClick={onRadioClick}
    />
  ) : (
    <Checkbox
      className={css.check}
      size="xs"
      checked={state === 'Checked'}
      disabled={disabled}
      intermediate={state === 'Indeterminate'}
      onChange={onCheckboxClick}
    />
  )
}

function getState(uid: string, tab?: TabObject) {
  if (!tab) return

  if (tab.context.some(ctx => ctx.treeId === uid)) return 'Checked'

  if (tab.halfContext.some(ctx => ctx.treeId === uid)) return 'Indeterminate'

  return
}

function isTreeIconKey(key: string): key is keyof typeof AllTreeIcons {
  if (key in AllTreeIcons) {
    return true
  }

  logIfNotTest(`Иконка дерева ${key} не найдена, возможно, стоит добавить ее в AllTreeIcons`)
  return false
}

export function getTreeNode(
  activeTab: TabObject | undefined,
  {
    icon,
    uid,
    name,
    children,
    properties,
    countChild,
    elementType,
    projectEntityUid,
    disabled: disabledProp,
    state,
  }: ProjectElementInfo,
  changeMenuVisibility: ChangeMenuVisibilityFuncType,
  onChangeStateChecked: (uid: ProjectElementInfo['uid']) => OnChangeStateChecked,
): DataNode {
  const objectTreeNode = {
    uid,
    name,
    elementType,
    projectEntityUid,
    properties,
    state,
  }

  const TreeIcon = isTreeIconKey(icon) ? AllTreeIcons[icon] : undefined
  const IconComponent = TreeIcon ? (
    <TreeIcon size="s" style={{ color: getFillColor(properties) }} />
  ) : undefined

  const checkedState = getState(uid, activeTab)
  const isLeaf = !(countChild > 0 || children.length > 0)
  const disabled = disabledProp ?? undefined

  return {
    key: uid,
    title: (
      <CustomLabel
        objectTreeNode={objectTreeNode}
        icon={IconComponent}
        isLeaf={isLeaf}
        check={getCheckComponent(
          activeTab?.type,
          elementType,
          onChangeStateChecked(uid),
          checkedState,
          disabled,
        )}
        changeMenuVisibility={changeMenuVisibility}
      />
    ),
    children: children.map(child =>
      getTreeNode(activeTab, child, changeMenuVisibility, onChangeStateChecked),
    ),
    isLeaf,
  }
}

function isReactElement(elem: unknown): elem is ReactElement {
  return isObject(elem) && 'props' in elem
}

export function isObjectTreeNodeProps(
  elem: unknown,
): asserts elem is ReactElement<{ objectTreeNode: ObjectTreeNode }> {
  if (
    isReactElement(elem) &&
    'props' in elem &&
    isObject(elem.props) &&
    'objectTreeNode' in elem.props
  ) {
    return
  }

  throw new Error('Объект не ObjectTreeNode')
}
